import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MessagingNotificationService } from '../../services/messaging-notification.service';
import { UnreadSmsService } from '../../../modules/messaging/services/unread-sms.service';
import { CrmContactGroupsService } from '../../../modules/messaging/services/crm-contact-groups.service';
import { CrmTaskService } from '../../services/v3/crm-task.service';
import { MIRROR_MODULES } from '../../../modules/crm-mirror/crm-mirror.config';

type CrmKey = 'customer' | 'carrier' | 'supplier' | 'contractor';

@Component({
  selector: 'app-side-bar',
  standalone: true,
  imports: [CommonModule, RouterModule, AngularSvgIconModule],
  templateUrl: './side-bar.component.html',
  styleUrl: './side-bar.component.css'
})
export class SideBarComponent implements OnInit {
  isSidebarHidden = window.innerWidth < 768;

  /* ---------------- collapsible sections / sub-groups ---------------- */

  /** Routes in each section ("s.*") and sub-group ("g.*") - the one holding
   *  the current page is opened on navigation, so the active link is never hidden. */
  private readonly navGroups: Record<string, string[]> = {
    's.general': ['/financial-dashboard', '/fleet-management', '/driver-availability', '/roster-management', '/shifts', '/jobs', '/consignments', '/driver-location', '/posts', '/client-invoice', '/driver-payments'],
    'g.dashboards': ['/financial-dashboard', '/fleet-management', '/driver-availability'],
    'g.operations': ['/roster-management', '/shifts', '/jobs', '/consignments', '/driver-location'],
    'g.finance': ['/client-invoice', '/driver-payments'],
    's.contacts': ['/clients', '/carriers', '/drivers', '/suppliers'],
    's.crm': ['/crm', '/messages', '/integrations', '/workflow'],
    'g.pipelines': ['/crm/customer', '/crm/carriers', '/crm/supplier', '/crm/contractor'],
    'g.service': ['/crm/tickets', '/crm/tasks', '/messages'],
    'g.sales': MIRROR_MODULES.map(m => `/crm/${m.key}`),
    'g.crmSetup': ['/crm/reporting', '/integrations', '/workflow'],
    's.compliance': ['/driver-compliance', '/incident-reports', '/expired-documents'],
    's.admin': ['/users', '/customer-hierarchy', '/customer-carrier-accounts', '/activity-log', '/settings'],
  };
  private static readonly NAV_STATE_KEY = 'custm.sidebar.collapsed';
  /** key -> true when collapsed; remembered per browser. */
  collapsed: Record<string, boolean> = SideBarComponent.loadCollapsed();

  private static loadCollapsed (): Record<string, boolean> {
    try { return JSON.parse(localStorage.getItem(SideBarComponent.NAV_STATE_KEY) || '{}') || {}; } catch { return {}; }
  }
  private saveCollapsed (): void {
    try { localStorage.setItem(SideBarComponent.NAV_STATE_KEY, JSON.stringify(this.collapsed)); } catch { /* storage unavailable */ }
  }

  isOpen (key: string): boolean { return !this.collapsed[key]; }

  toggleNav (key: string): void {
    this.collapsed = { ...this.collapsed, [key]: !this.collapsed[key] };
    this.saveCollapsed();
  }

  private get sectionKeys (): string[] { return Object.keys(this.navGroups).filter(k => k.startsWith('s.')); }

  get allCollapsed (): boolean { return this.sectionKeys.every(k => this.collapsed[k]); }

  /** Collapse every section except the one holding the current page, or expand them all. */
  toggleAllNav (): void {
    this.collapsed = this.allCollapsed ? {} : Object.fromEntries(this.sectionKeys.map(k => [k, true]));
    if (!this.allCollapsed) this.revealActive();
    this.saveCollapsed();
  }

  private onRoute (url: string, routes: string[]): boolean {
    return routes.some(r => url === r || url.startsWith(r + '/'));
  }

  /** A sub-group whose page is open - its header is highlighted (its links live in the flyout). */
  isGroupActive (key: string): boolean {
    return this.onRoute(this.router.url.split(/[?#]/)[0], this.navGroups[key] ?? []);
  }

  /* ---- sub-group flyouts (floating menu beside the side bar) ---- */

  flyout: { key: string; top: number; left: number } | null = null;
  /** Opened by a click - stays until a click elsewhere, not just on mouse-out. */
  private flyoutPinned = false;
  private flyoutTimer: ReturnType<typeof setTimeout> | null = null;

  private placeFlyout (key: string, event: Event, items: number): void {
    const head = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const sidebar = (event.currentTarget as HTMLElement).closest('.sidebar-custm')?.getBoundingClientRect();
    const height = 52 + items * 38;
    const top = Math.max(8, Math.min(head.top - 6, window.innerHeight - height - 8));
    this.flyout = { key, top, left: (sidebar?.right ?? head.right) + 6 };
  }

  toggleFlyout (key: string, event: Event, items: number): void {
    event.stopPropagation();
    this.cancelCloseFlyout();
    if (this.flyout?.key === key && this.flyoutPinned) { this.closeFlyout(); return; }
    this.placeFlyout(key, event, items);
    this.flyoutPinned = true;
  }

  hoverFlyout (key: string, event: Event, items: number): void {
    this.cancelCloseFlyout();
    if (this.flyout?.key === key) return;
    this.placeFlyout(key, event, items);
    this.flyoutPinned = false;
  }

  scheduleCloseFlyout (): void {
    if (this.flyoutPinned) return;
    this.cancelCloseFlyout();
    this.flyoutTimer = setTimeout(() => this.closeFlyout(), 220);
  }

  cancelCloseFlyout (): void {
    if (this.flyoutTimer) { clearTimeout(this.flyoutTimer); this.flyoutTimer = null; }
  }

  closeFlyout (): void {
    this.cancelCloseFlyout();
    this.flyout = null;
    this.flyoutPinned = false;
  }

  @HostListener('document:click')
  @HostListener('document:keydown.escape')
  @HostListener('window:resize')
  onOutside (): void { if (this.flyout) this.closeFlyout(); }

  private revealActive (): void {
    const url = this.router.url.split(/[?#]/)[0];
    let changed = false;
    for (const key of this.sectionKeys) {
      if (this.collapsed[key] && this.onRoute(url, this.navGroups[key])) {
        this.collapsed = { ...this.collapsed, [key]: false };
        changed = true;
      }
    }
    if (changed) this.saveCollapsed();
  }
  messagesUnreadCount = 0;
  messagesPulse = false;

  /** Which CRM menu items each unread-SMS number (last 9 digits) belongs
   *  to, resolved server-side for just those numbers - the sidebar used to
   *  load all four boards in full on every page to match phones itself
   *  (the contractor feed alone is ~20k rows and hit the 30s timeout). */
  private phoneCategories = new Map<string, CrmKey[]>();

  /** Latest unread SMS count per CRM menu item, and which ones just went
   *  up (briefly pulse so a new text is noticed). */
  crmUnread: Record<CrmKey, number> = { customer: 0, carrier: 0, supplier: 0, contractor: 0 };
  crmPulse: Record<CrmKey, boolean> = { customer: false, carrier: false, supplier: false, contractor: false };

  get crmUnreadTotal (): number {
    return Object.values(this.crmUnread).reduce((sum, n) => sum + n, 0);
  }

  /** Dot on the collapsed sidebar's menu button. */
  get hasAnyUnread (): boolean {
    return this.messagesUnreadCount > 0 || this.crmUnreadTotal > 0;
  }

  badge (count: number): string {
    return count > 9 ? '9+' : String(count);
  }

  private setCrmUnread (key: CrmKey, count: number): void {
    const previous = this.crmUnread[key];
    this.crmUnread[key] = count;
    if (count > previous && this.primedCrmUnread[key]) {
      this.crmPulse[key] = true;
      setTimeout(() => this.crmPulse[key] = false, 2600);
    }
    this.primedCrmUnread[key] = true;
  }

  private primedCrmUnread: Record<CrmKey, boolean> = { customer: false, carrier: false, supplier: false, contractor: false };

  /** Splits the unread SMS counts across the CRM menu items, looking up
   *  only numbers not already classified. */
  private watchCrmUnread (): void {
    this.unreadSmsService.counts$.pipe(
      debounceTime(300),
      switchMap((counts) => {
        const unknown = Object.keys(counts).filter((key) => !this.phoneCategories.has(key)).slice(0, 200);
        if (!unknown.length) return of(counts);
        return this.crmContactGroups.getPhoneCategories(unknown).pipe(
          tap((found) => unknown.forEach((key) => this.phoneCategories.set(key, (found[key] ?? []) as CrmKey[]))),
          map(() => counts),
          catchError(() => of(counts))
        );
      })
    ).subscribe((counts) => {
      const totals: Record<CrmKey, number> = { customer: 0, carrier: 0, supplier: 0, contractor: 0 };
      Object.entries(counts).forEach(([key, count]) => {
        (this.phoneCategories.get(key) ?? []).forEach((category) => totals[category] += count);
      });
      (Object.keys(totals) as CrmKey[]).forEach((key) => this.setCrmUnread(key, totals[key]));
    });
  }

  toggleSidebar() {
    this.isSidebarHidden = !this.isSidebarHidden;
  }
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    const screenWidth = (event.target as Window).innerWidth;
    this.isSidebarHidden = screenWidth < 768;
  }

  constructor(
    private router: Router,
    private authService: AuthService,
    private messagingNotificationService: MessagingNotificationService,
    private unreadSmsService: UnreadSmsService,
    private crmContactGroups: CrmContactGroupsService,
    private crmTasks: CrmTaskService
  ) {
  }

  /** CRM > Tasks badge: my tasks overdue or due today. */
  /** CRM > Tasks is hidden for now (the page still works at /crm/tasks, and tasks are still created). */
  readonly showTasks = false;
  /** CRM > Sales & marketing - the modules mirroring Zoho CRM (crm-mirror.config.ts).
   *  Hidden for now (the /crm/<module> pages still work). */
  readonly showSalesMarketing = false;
  readonly salesModules = MIRROR_MODULES;
  /** Admin > Customer Hierarchy / Customer Carrier Accounts are hidden for now (routes still work). */
  readonly showCustomerAccounts = false;
  tasksDueCount = 0;
  tasksOverdue = 0;
  /** Service > Tickets badge: open tickets assigned to me (pulses when one is added). */
  ticketsAssignedCount = 0;
  ticketsPulse = false;
  private ticketsPrimed = false;
  private lastTasksRefresh = 0;

  /** The Service menu's badge: unread messages + tickets assigned to me. */
  get serviceBadgeCount (): number { return this.messagesUnreadCount + this.ticketsAssignedCount; }
  get serviceBadgeTitle (): string {
    const parts = [];
    if (this.ticketsAssignedCount) parts.push(`${this.ticketsAssignedCount} open ticket(s) assigned to you`);
    if (this.messagesUnreadCount) parts.push(`${this.messagesUnreadCount} unread messages`);
    return parts.join(', ');
  }

  /** My tasks (when Tasks is shown) and my assigned tickets - one light summary call. */
  private refreshTasksBadge (force = false): void {
    if (!force && Date.now() - this.lastTasksRefresh < 60_000) return;
    this.lastTasksRefresh = Date.now();
    this.crmTasks.summary('mine', null, true).subscribe({
      next: s => {
        this.tasksOverdue = s.overdue;
        this.tasksDueCount = s.overdue + s.dueToday;
        const assigned = s.assignedTickets ?? 0;
        if (this.ticketsPrimed && assigned > this.ticketsAssignedCount) {
          this.ticketsPulse = true;
          setTimeout(() => this.ticketsPulse = false, 2600);
        }
        this.ticketsPrimed = true;
        this.ticketsAssignedCount = assigned;
      },
      error: () => {}
    });
  }
  ngOnInit(): void {
    // Refresh the Tasks / assigned-tickets badges now, on navigation (at most once a minute) and every minute.
    this.refreshTasksBadge(true);
    this.router.events.subscribe(e => { if (e instanceof NavigationEnd) { this.refreshTasksBadge(); this.revealActive(); this.closeFlyout(); } });
    this.revealActive();
    setInterval(() => this.refreshTasksBadge(true), 60_000);
    // The side bar only renders once the admin is authenticated (see
    // app.component.html), and it persists across route changes within the
    // authenticated layout - so this is the natural place to start the
    // app-wide "new message" watcher for the badge/toast/sound.
    this.messagingNotificationService.start();
    let primedMessages = false;
    this.messagingNotificationService.unreadCount$.subscribe((count) => {
      if (primedMessages && count > this.messagesUnreadCount) {
        this.messagesPulse = true;
        setTimeout(() => this.messagesPulse = false, 2600);
      }
      primedMessages = true;
      this.messagesUnreadCount = count;
    });

    this.watchCrmUnread();
  }

  public get isEcommerce () {
    return this.authService.isEcommerce;
  }
}
